import Component from '@glimmer/component';
import { tracked, cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import { Button } from 'tio-ui/components/buttons';
import { Modal } from 'tio-ui/components/modal';
import { Form, Input, Textarea, Select } from 'tio-ui/components/forms';
import { VStack } from 'tio-ui/components/layout';
import { and, eq, or } from 'tio-ui/utilities';
import { trackedFunction } from 'reactiveweb/function';
import PartnerVisibilityConfig from './partner-visibility-config.gts';
import * as v from 'valibot';
import type { FormResultData } from 'tio-ui/components/forms';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type Report from '../models/reports';
import type CompanyModel from 'tio-common/models/company';
import type Owner from '@ember/owner';

/**
 * The typed form data structure for the save report dialog.
 * This interface defines the exact shape of data passed through FormResultData<SaveReportFormData>
 */
interface SaveReportFormData {
  name: string;
  description: string;
  visibilityLevel: string;
}

interface SaveReportDialogSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    onSave: (report: Report) => void;

    // The raw data from the search builder (flexible JSON)
    searchData: Record<string, unknown>;

    // Optional serialized query parameters for URL rehydration
    serializedQuery?: string;

    reportType: 'chart_builder' | 'table_builder';

    // Current report being viewed (if any)
    currentReport?: Report;

    // Whether to show partner visibility options (only for global/cross-company context)
    showPartnerVisibility?: boolean;
  };
}

export default class SaveReportDialog extends Component<SaveReportDialogSignature> {
  @service declare store: StoreService;

  @tracked isSaving = false;
  @tracked errors: Record<string, string[]> = {};
  @tracked saveMode: 'update' | 'create_new' = 'update';
  @tracked partnerAccessConfig: number[] = [];
  @tracked currentVisibilityLevel = 'private';

  constructor(owner: Owner, args: SaveReportDialogSignature['Args']) {
    super(owner, args);

    // Initialize currentVisibilityLevel from existing report if available
    if (args.currentReport?.visibilityLevel) {
      this.currentVisibilityLevel = args.currentReport.visibilityLevel;

      // Initialize partner access config if it exists
      if (args.currentReport.partnerAccessConfig) {
        this.partnerAccessConfig = [...args.currentReport.partnerAccessConfig];
      }
    }
  }

  FormSchema = v.object({
    name: v.pipe(v.string(), v.trim(), v.minLength(1, 'Name is required')),
    description: v.pipe(v.string(), v.trim()),
    visibilityLevel: v.picklist(['private', 'partner', 'global']),
  });

  // Load available companies for partner visibility configuration
  availableCompanies = trackedFunction(this, async () => {
    return this.store.query<CompanyModel>('company', {
      filter: {
        'is-active': 1,
      },
      sort: 'name',
      page: {
        limit: 400,
      },
    }) as Promise<CompanyModel[]>;
  });

  get visibilityOptions() {
    const options = [
      { key: 'private', label: 'Private (Only me)' },
      { key: 'global', label: 'Global (Everyone)' },
    ];

    if (this.args.showPartnerVisibility) {
      options.splice(1, 0, { key: 'partner', label: 'Partner Shared' });
    }

    return options;
  }

  get saveModeOptions() {
    return [
      { key: 'update', label: `Update "${this.args.currentReport?.displayName}"` },
      { key: 'create_new', label: 'Save as new report' },
    ];
  }

  get hasCurrentReport() {
    return Boolean(this.args.currentReport);
  }

  @cached
  get defaultFormData() {
    if (this.hasCurrentReport && this.saveMode === 'update') {
      const currentReport = this.args.currentReport!;
      return {
        name: currentReport.name || '',
        description: currentReport.description || '',
        visibilityLevel: currentReport.visibilityLevel || 'private',
      };
    }

    // Default form data for new reports or when copying an existing report
    return {
      name: this.hasCurrentReport ? `${this.args.currentReport!.name} (Copy)` : '',
      description: this.hasCurrentReport ? this.args.currentReport!.description || '' : '',
      visibilityLevel: 'private',
    };
  }

  onSubmit = async (result: FormResultData<SaveReportFormData>, _event: SubmitEvent) => {
    // No casting needed - FormResultData is properly typed with SaveReportFormData
    const { data } = result;

    this.isSaving = true;
    try {
      let report: Report;

      // Extract serializedQuery from searchData if not provided separately
      const serializedQuery =
        this.args.serializedQuery || (this.args.searchData?.urlParams as string) || '';

      // Create properly structured search config based on report type
      const searchConfig = this.createSearchConfig(this.args.searchData, this.args.reportType);

      if (this.hasCurrentReport && this.saveMode === 'update') {
        // Update existing report
        const currentReport = this.args.currentReport!;
        // Update the existing report's attributes
        currentReport.name = data.name;
        currentReport.description = data.description || '';
        currentReport.visibilityLevel = data.visibilityLevel;
        currentReport.partnerAccessConfig = this.partnerAccessConfig;
        currentReport.searchConfig = searchConfig;
        currentReport.metadata = {
          saved_at: new Date().toISOString(),
          serializedQuery: serializedQuery,
        };

        await currentReport.save();
        report = currentReport;
      } else {
        // Create new report
        report = this.store.createRecord('report', {
          ...data,
          partnerAccessConfig: this.partnerAccessConfig,
          reportType: this.args.reportType,
          searchConfig: searchConfig,
          metadata: {
            saved_at: new Date().toISOString(),
            serializedQuery: serializedQuery,
          },
        }) as Report;

        await report.save();
      }

      // Notify parent
      this.args.onSave(report);
      this.args.onClose();
    } catch (error) {
      // Handle error - could set a general error message
      console.error('Failed to save report:', error);
    } finally {
      this.isSaving = false;
    }
  };

  updateSaveMode = (key: string) => {
    if (key) {
      this.saveMode = key as 'update' | 'create_new';
    }
  };

  handleCancel = () => {
    this.args.onClose();
  };

  handlePartnerAccessChange = (config: number[]) => {
    this.partnerAccessConfig = config;
  };

  // No-op since visibility is handled by the main form
  handleVisibilityLevelChange = () => {};

  handleFormChange = (result: FormResultData<SaveReportFormData>, _event: Event) => {
    // No casting needed - FormResultData is properly typed
    const { data } = result;

    // Update visibility level immediately for UI reactivity
    if (data.visibilityLevel) {
      this.currentVisibilityLevel = data.visibilityLevel;
    }

    // Validate on input changes
    this.validateField(result);
  };

  validateField = (result: FormResultData<SaveReportFormData>) => {
    const parseResult = v.safeParse(this.FormSchema, result.data);

    if (parseResult.success) {
      this.errors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};

      for (const issue of parseResult.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.errors = validationErrors;
    }
  };

  /**
   * Create properly structured search config based on report type
   * Separates chart settings from actual query filters
   */
  createSearchConfig = (
    searchData: Record<string, unknown>,
    _reportType: 'chart_builder' | 'table_builder'
  ): Record<string, unknown> => {
    // Use the searchData as-is since it's already properly structured
    // from reportSearchConfig in the parent component
    return searchData;
  };

  <template>
    <Modal @isOpen={{@isOpen}} @onClose={{this.handleCancel}} @size="lg" as |m|>
      <m.Header>
        Save
        {{if (eq @reportType "chart_builder") "Chart" "Table"}}
        Report
      </m.Header>

      <m.Body>
        <Form @onChange={{this.handleFormChange}} @onSubmit={{this.onSubmit}} id="save-report-form">
          <VStack class="flex flex-col gap-y-4">
            {{#if this.hasCurrentReport}}
              <Select
                @name="saveMode"
                @label="Save Option"
                @items={{this.saveModeOptions}}
                @selectedKey={{this.saveMode}}
                @onAction={{this.updateSaveMode}}
              />
            {{/if}}

            <Input
              @name="name"
              @label="Report Name"
              @value={{this.defaultFormData.name}}
              @isRequired={{true}}
              @errors={{this.errors.name}}
            />

            <Textarea
              @name="description"
              @label="Description (Optional)"
              @value={{this.defaultFormData.description}}
              @errors={{this.errors.description}}
            />

            <Select
              @name="visibilityLevel"
              @label="Visibility"
              @items={{this.visibilityOptions}}
              @selectedKey={{this.defaultFormData.visibilityLevel}}
              @errors={{this.errors.visibilityLevel}}
            />

            {{#if (eq this.currentVisibilityLevel "partner")}}
              <PartnerVisibilityConfig
                @visibilityLevel="partner"
                @partnerAccessConfig={{this.partnerAccessConfig}}
                @availableCompanies={{or this.availableCompanies.value undefined}}
                @onVisibilityLevelChange={{this.handleVisibilityLevelChange}}
                @onPartnerAccessConfigChange={{this.handlePartnerAccessChange}}
              />
            {{/if}}
          </VStack>
        </Form>
      </m.Body>

      <m.Footer>
        <div class="flex justify-end gap-x-3">
          <Button @appearance="outlined" @onPress={{this.handleCancel}} @label="Cancel" />

          <Button
            type="submit"
            form="save-report-form"
            @intent="primary"
            @isRunning={{this.isSaving}}
          >
            {{#if this.isSaving}}
              Saving...
            {{else if (and this.hasCurrentReport (eq this.saveMode "update"))}}
              Update Report
            {{else}}
              Save New Report
            {{/if}}
          </Button>
        </div>
      </m.Footer>
    </Modal>
  </template>
}
